import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { SortableContainer } from 'react-sortable-hoc';
import { Flex, Typography, Box, Button, Card, CardTitle, CardContent, color } from '@kistech/kis-ui';
import { selectors as configurationsSelectors } from 'store/configurations';
import { MediaResponse } from 'views/MediaCenterView/types';
import { SortableRow } from '../MediaRow';
import { TransitionTimeDrawer } from './TransitionTimeDrawer';
import { ImagePreview } from '../ImagePreview';
import { MediaText } from '../MediaText';
import { SlideshowImageListProps } from '../../../types';

export const SlideshowImageList: React.FC<SlideshowImageListProps> = (props: SlideshowImageListProps) => {
  const { id, slideShowImages, activeImage, previewUrl, isActive, mediaType, parentObj, configuration } = props;
  const [showSlideTime, setShowSlideTime] = useState(false);
  const loading = useSelector(configurationsSelectors.selectPending);
  const isAddImageDisabled = slideShowImages && slideShowImages.length >= 10;
  const hasImages = Boolean(slideShowImages && slideShowImages.length);
  const transitionTime = (parentObj && parentObj.transitionTime) || 5;

  const onMakeActive = () => {
    props.updateConfig({ type: mediaType });
  };

  const SortableList = SortableContainer(() => {
    return (
      <Box style={{ maxHeight: '329px', overflowY: 'auto' }}>
        {activeImage &&
          slideShowImages.map((image, idx) => (
            <SortableRow
              index={idx}
              key={image.s3Name}
              label={image.name}
              selected={Boolean(activeImage && activeImage.s3Name === image.s3Name)}
              cursor={'grab'}
              onClick={() => {
                if (activeImage && activeImage.id !== image.id) {
                  props.setActiveImage(image);
                }
              }}
              onEditClick={() => props.onImageEditClick(image.s3Name, image.name, image.id)}
              onDeleteClick={() => props.onDelete(image)}
            />
          ))}
      </Box>
    );
  });

  const arrayMove = (arr: MediaResponse[], fromIndex: number, toIndex: number): MediaResponse[] => {
    const newArr = [...arr];
    const element = newArr[fromIndex];
    newArr.splice(fromIndex, 1);
    newArr.splice(toIndex, 0, element);

    return newArr;
  };

  const onSortEnd = (sortProps: { oldIndex: number; newIndex: number }) => {
    if (sortProps.oldIndex !== sortProps.newIndex) {
      const newOrder = arrayMove(slideShowImages, sortProps.oldIndex, sortProps.newIndex).map((item) => item.s3Name);
      props.updateConfig({ order: newOrder });
    }
  };

  return (
    <>
      {hasImages ? (
        <>
          <TransitionTimeDrawer
            configuration={configuration}
            isOpen={showSlideTime}
            transitionTime={transitionTime}
            mediaType={mediaType}
            parentObj={parentObj}
            configurationId={id}
            onClose={() => setShowSlideTime(false)}
          />
          <Flex fullWidth noWrap>
            <Box width={650}>
              <Card raised hasBorderRadius>
                <CardTitle
                  title={
                    <Flex direction="column" align="start" fullWidth>
                      <Flex direction="row" fullWidth align="center" justify="between">
                        <Typography size={18} weight={600}>
                          Slideshow
                        </Typography>
                        {slideShowImages.length > 1 && (
                          <Button
                            size="x-small"
                            appearance="primary"
                            onClick={() => setShowSlideTime(true)}
                            isLoading={loading}
                            data-testid="add-image-button"
                          >
                            Set Slide Time
                          </Button>
                        )}
                        {!isAddImageDisabled && (
                          <Button
                            size="x-small"
                            appearance="primary"
                            onClick={props.onAddImageClick}
                            isLoading={loading}
                            data-testid="add-image-button"
                          >
                            Add Image
                          </Button>
                        )}
                        <Flex>
                          {isActive && !loading ? (
                            <Typography size={18} weight={600} color={color.PRIMARY}>
                              ACTIVE
                            </Typography>
                          ) : (
                            <Button size="x-small" appearance="primary" onClick={onMakeActive} isLoading={loading}>
                              Make Active
                            </Button>
                          )}
                        </Flex>
                      </Flex>
                    </Flex>
                  }
                  gutter={5}
                />
                <CardContent gutter={0}>
                  {slideShowImages.length > 1 ? (
                    <Box margin="2 4" fullWidth style={{ textAlign: 'center' }}>
                      <Typography>Drag and drop an image to reorder your slideshow</Typography>
                    </Box>
                  ) : null}
                  <SortableList onSortEnd={onSortEnd} distance={1} />
                </CardContent>
              </Card>
              {isAddImageDisabled && (
                <Box margin="4 0">
                  <Typography color={color.RED}>You can have up to 10 images in your slideshow.</Typography>
                </Box>
              )}
            </Box>
            {activeImage && (
              <ImagePreview
                previewUrl={previewUrl}
                activeImage={activeImage && activeImage.name}
                imageType={mediaType}
              />
            )}
          </Flex>
        </>
      ) : (
        <Box width={650}>
          <Card raised hasBorderRadius>
            <CardTitle
              title={
                <Typography size={18} weight={600}>
                  Slideshow
                </Typography>
              }
              gutter={5}
            />
            <CardContent>
              <Box>
                <MediaText mediaType={mediaType} />
                <Box height={20} />
                <Button
                  size="small"
                  width={160}
                  appearance="primary"
                  onClick={props.onAddImageClick}
                  data-testid="add-image-button"
                >
                  Add Image
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </>
  );
};
